import React from "react";
import clsx from "clsx";
import {
  ButtonVariant,
  CodeSnippet,
  Menu,
  MenuItem,
  Text,
} from "@merge-api/merge-javascript-shared";
import { ChevronDown } from "lucide-react";

interface ToolOption {
  label: string;
  value: string;
}

interface Props {
  options: ToolOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  code: string;
  language?: string;
  ariaLabel: string;
}

const ToolTabbedCodeSnippet = ({
  options,
  selectedValue,
  onSelect,
  code,
  language,
  ariaLabel,
}: Props) => {
  // derived state
  const selectedLabel = options.find((o) => o.value === selectedValue)?.label ?? options[0]?.label;

  // render
  return (
    <div className="relative">
      <CodeSnippet variant="gray" showCopyButton language={language}>
        {code}
      </CodeSnippet>

      {/* Tool tabs overlaid on the header's left side. Shown at md and up; on narrower
          widths they collapse to the dropdown below. right-24 reserves room for the
          language label + copy icon. */}
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="pointer-events-none absolute top-0 left-0 right-24 hidden h-10 flex-row items-center gap-1.5 overflow-x-hidden px-3 md:flex"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selectedValue === option.value}
            onClick={() => onSelect(option.value)}
            className={clsx(
              "pointer-events-auto shrink-0 rounded-full px-2.5 py-0 transition-colors",
              selectedValue === option.value
                ? "bg-gray-80 text-white"
                : "text-gray-20 hover:text-white",
            )}
          >
            <Text variant="h6" as="span">
              {option.label}
            </Text>
          </button>
        ))}
      </div>

      {/* Below md the tabs collapse into a dropdown matching the header style. */}
      <div className="absolute top-0 left-0 flex h-10 flex-row items-center px-3 md:hidden">
        <Menu
          ButtonProps={{
            variant: ButtonVariant.TextBlack,
            className:
              "!px-0 !py-0 text-gray-10 hover:enabled:!bg-transparent [&:hover]:!text-gray-50",
            children: (
              <>
                <Text variant="h6" as="span">
                  {selectedLabel}
                </Text>
                <ChevronDown size={16} className="ml-1.5" />
              </>
            ),
          }}
          menuPlacement="bottom-start"
          menuVariant="gray"
          menuSize="sm"
        >
          {options.map((option) => (
            <MenuItem key={option.value} onClick={() => onSelect(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </div>
  );
};

export default ToolTabbedCodeSnippet;
